import { useEffect, useState, useContext, useMemo } from "react";
import { Modal } from "react-bootstrap";

import BottomIconHideWrapper from "@stadium/shared/src/components/bottom-icon-hide-wrapper";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import useFetchTaxon from "@hooks/use-fetch-taxon";

import RestrictionsOptions from "@components/store/product-detail/shared/restrictions-option-selector/";

import { NO_RESTRICTION_OPTION, NO_RESTRICTION_OPTION_ID } from "@constants/products/detail";
import AddToBagBtn from "@components/store/product-detail/shared/add-to-bag";
import UpdateExistingCartItemPreferencesBtn from "@components/store/product-detail/shared/update-cart-item-collection-product";

import modalDtry from "./main.module.scss";

const emptyArr = [];
const emptyObj = {};

const useUSRestrictionsModal = (defaultSelectedRestriction = emptyArr) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRestrictions, setSelectedRestrictions] = useState([]);

  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const { restrictions } = useFetchTaxon();

  //default selected restrictions when the modal closes/opens
  useEffect(() => {
    setSelectedRestrictions(
      defaultSelectedRestriction.map((restriction) => restriction.toString()),
    );
  }, [defaultSelectedRestriction, isOpen]);

  const toggleRestrictionsModal = () => setIsOpen((prevState) => !prevState);

  const handleChange = (id) => {
    if (id == NO_RESTRICTION_OPTION_ID) {
      setSelectedRestrictions(emptyArr);
      return;
    }
    if (selectedRestrictions.includes(id))
      setSelectedRestrictions(selectedRestrictions.filter((elmt) => elmt !== id));
    else setSelectedRestrictions([...selectedRestrictions, id]);
  };

  const successHandler = ({ onSuccessCallback } = {}) => {
    onSuccessCallback?.();
    toggleRestrictionsModal();
  };

  const renderModalBody = () => (
    <div className={modalDtry.modalRow}>
      <RestrictionsOptions
        options={[NO_RESTRICTION_OPTION, ...restrictions]}
        selectedData={selectedRestrictions}
        handleChange={handleChange}
      />
    </div>
  );

  const selectedRestrictionObjects = useMemo(
    () =>
      restrictions
        .filter((r) => selectedRestrictions.includes(String(r.id)))
        .map((r) => ({ id: r.id, name: r.name })),
    [restrictions, selectedRestrictions],
  );

  const renderAddToBagBtn = ({ product, quantity, variant }) => (
    <AddToBagBtn
      product={product}
      quantity={quantity}
      variant={variant}
      restrictions={selectedRestrictionObjects}
      onSuccessCallback={successHandler}
    />
  );

  const renderUpdateExistingItemPreferencesBtn = ({ lineItem, onSuccessCallback }) => (
    <UpdateExistingCartItemPreferencesBtn
      lineItem={lineItem}
      restrictions={selectedRestrictionObjects}
      onSuccessCallback={() => successHandler({ onSuccessCallback })}
    />
  );

  const renderRestrictionsModal = ({
    product = emptyObj,
    quantity = null,
    variant = emptyObj,
    lineItem = emptyObj,
    isUpdateExistingItemInCart = false,
    onSuccessCallback = () => {},
  }) => {
    return (
      <Modal
        data-acsb-overlay="popup"
        show={isOpen}
        animation={false}
        dialogClassName={`${modalDtry.modalDialog}`}
        className={`${modalDtry.dietaryModal}`}
        onHide={toggleRestrictionsModal}
      >
        <Modal.Header className={modalDtry.modalHeader}>
          <i className={modalDtry.iconClose} onClick={toggleRestrictionsModal} />
          <div className={modalDtry.modalHeading}>Select Dietary Restriction(s)</div>
          <p className={modalDtry.modalSubHeading}>
            *Based on the amount of dietary restrictions, number of items may vary.
          </p>
        </Modal.Header>
        <Modal.Body className={modalDtry.modalBody}>
          <BottomIconHideWrapper hideIcon={isMobileDevice}>
            {renderModalBody()}
          </BottomIconHideWrapper>
        </Modal.Body>
        <Modal.Footer className={`${modalDtry.modalFooter} ${modalDtry.extraClass}`}>
          {isUpdateExistingItemInCart
            ? renderUpdateExistingItemPreferencesBtn({ lineItem, onSuccessCallback })
            : renderAddToBagBtn({ product, quantity, variant })}
        </Modal.Footer>
      </Modal>
    );
  };

  return { selectedRestrictions, toggleRestrictionsModal, renderRestrictionsModal };
};

export default useUSRestrictionsModal;
